/**
 * Grouped activity grid for city pages with no category filter.
 *
 * Renders one section per category present in the city ("Activities",
 * "Excursions", "Stays"…), each showing top N cards plus a "See all" link
 * to /category/{slug}/{city}. Categories with no listings are omitted.
 *
 * Replaces the flat ActivityGrid when the user is browsing the city as a
 * whole — gives a richer overview than a single paginated stream.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { activitiesApi } from "../api/activities.api";
import { activityKeys } from "../constants/query-keys";
import { ShelfArrows, useShelfScroll } from "../../../shared/ui/scroll-shelf";
import { ActivityCardDiscover } from "./activity-card-discover";
import { rankCategories } from "../lib/category-rank";

import { Icon } from "@majourneys/icons";
interface ActivityListItem {
  isNew?: boolean;
  id: string;
  slug: string;
  title: string;
  images: string[];
  price: number;
  typePrice?: number;
  rating?: number;
  category: { id: string; name: string; slug: string } | null;
  city: { id: string; name: string; slug: string };
  segment?: string | null;
  mode?: string;
  /** Venue tags; a restaurant's cuisine names it on the card. */
  tags?: readonly string[] | null;
}

interface CityCategoryGroupsProps {
  items: ActivityListItem[];
  lang: string;
  citySlug: string;
  cardsPerGroup?: number;
}

/**
 * Roll specialty subcategories up to their parent grouping. The DB stores
 * narrow slugs ("quad", "horse", "camel") that all belong under the broader
 * "activities" umbrella for display purposes. Keeps the city overview from
 * fragmenting into many one-card sections.
 */
const PARENT_SLUG: Record<string, string> = {
  quad: "activities",
  horse: "activities",
  camel: "activities",
  "city-tour": "activities",
  air: "activities",
  "hot-air-balloon": "activities",
  "electric-scooter": "activities",
  bike: "activities",
  buggy: "activities",
  surf: "activities",
  "day-trips": "trips",
  villa: "stays",
  riad: "stays",
  hotel: "stays",
  hammam: "spas",
  crafts: "workshops",
  "calligraphy-painting": "workshops",
  "cooking-class": "workshops",
  pottery: "workshops",
  weaving: "workshops",
  zellige: "workshops",
};

const PARENT_LABEL: Record<string, string> = {
  activities: "Activities",
  excursions: "Excursions",
  trips: "Trips",
  stays: "Stays",
  spas: "Spas",
  pools: "Pools",
  boats: "Boats",
  workshops: "Workshops",
};

interface Group {
  slug: string;
  name: string;
  items: ActivityListItem[];
  /** True when this group merges multiple DB slugs (parent rollup). */
  composite: boolean;
}

function resolveSlug(rawSlug: string): string {
  return PARENT_SLUG[rawSlug] ?? rawSlug;
}

function resolveLabel(slug: string, fallbackName: string): string {
  return PARENT_LABEL[slug] ?? fallbackName;
}

function groupByCategory(items: ActivityListItem[]): Group[] {
  const map = new Map<string, Group>();
  const sourceSlugs = new Map<string, Set<string>>();
  for (const it of items) {
    if (!it.category) continue;
    const slug = resolveSlug(it.category.slug);
    const sources = sourceSlugs.get(slug) ?? new Set<string>();
    sources.add(it.category.slug);
    sourceSlugs.set(slug, sources);
    const existing = map.get(slug);
    if (existing) {
      existing.items.push(it);
    } else {
      map.set(slug, {
        slug,
        name: resolveLabel(slug, it.category.name),
        items: [it],
        composite: false,
      });
    }
  }
  for (const [slug, group] of map) {
    const sources = sourceSlugs.get(slug);
    group.composite = !!sources && (sources.size > 1 || !sources.has(slug));
  }
  // A city's sections run by how many places each has there, most first, with
  // the app's ranking breaking ties (owner, 2026-10-06), like its tab rail.
  const groups = [...map.values()];
  return rankCategories(groups, new Map(groups.map((g) => [g.slug, g.items.length])));
}

export function CityCategoryGroups({
  items,
  lang,
  citySlug,
  cardsPerGroup = 9,
}: CityCategoryGroupsProps) {
  const { i18n } = useTranslation();
  // Not the suspense hook: the icons are decoration, so the shelves render at
  // once and the icons appear when the (usually already cached) list lands.
  const { data: categoriesResponse } = useQuery({
    queryKey: [...activityKeys.categories(citySlug), i18n.language],
    queryFn: () => activitiesApi.listCategories(citySlug, i18n.language),
    staleTime: 5 * 60 * 1000,
  });
  const iconBySlug = new Map<string, string>();
  for (const c of (categoriesResponse?.data as Array<{ slug: string; icon?: string | null }> | undefined) ?? []) {
    if (c.icon) iconBySlug.set(c.slug, c.icon);
  }

  if (items.length === 0) return null;
  const groups = groupByCategory(items);
  if (groups.length === 0) return null;

  return (
    <div className="flex flex-col gap-12">
      {groups.map((group) => (
        <CategoryShelf
          key={group.slug}
          group={group}
          icon={iconBySlug.get(group.slug)}
          cityName={items[0]?.city.name ?? ""}
          lang={lang}
          citySlug={citySlug}
          cardsPerGroup={cardsPerGroup}
        />
      ))}
    </div>
  );
}

/**
 * One category row. Split out of the map so each row can own its own scroll
 * state: a hook cannot be called inside a loop, and the arrows need to know
 * about their row specifically.
 */
function CategoryShelf({
  group,
  icon,
  cityName,
  lang,
  citySlug,
  cardsPerGroup,
}: {
  group: ReturnType<typeof groupByCategory>[number];
  /** The category's 3D icon (API-served), floated after the title. */
  icon?: string;
  cityName: string;
  lang: string;
  citySlug: string;
  cardsPerGroup: number;
}) {
  const { t } = useTranslation();
  /* One card plus its gap, so a press advances by a whole card rather than
     leaving one sliced down the middle. */
  const shelf = useShelfScroll({ step: 250 });
  // The group's name in the page's language, exactly as written there (never
  // lowercased: German nouns keep their capital, "Alle Boote", not "boats").
  const label = t("activity.category_" + group.slug, { defaultValue: group.name });
  const seeAllAria = t("activity.see_all_places_aria", { count: group.items.length, category: label, city: cityName });

  return (
    <section aria-label={t("activity.category_in_city", { category: label, city: cityName })}>
          <div className="mb-4 flex items-end justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <h2
                className="flex min-w-0 items-center gap-1.5 text-[20px] font-bold leading-tight text-[#110E39] sm:text-[22px]"
                style={{ fontFamily: "var(--font-geist)" }}
              >
                <span className="min-w-0">{label}</span>
                {icon && (
                  <img
                    src={icon}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    decoding="async"
                    width={40}
                    height={40}
                    className="mj-title-icon h-9 w-9 shrink-0 object-contain sm:h-10 sm:w-10"
                  />
                )}
              </h2>
              <span className="hidden text-[13px] font-semibold text-[#5a5d77] sm:inline">
                {group.items.length}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Beside "See all", never over the row: an arrow inside the
                  row sits on top of the first card and hides the thing the
                  reader came to look at. */}
              <ShelfArrows shelf={shelf} />
              {!group.composite && (
              <Link
                to="/$lang/category/$category/$city"
                params={{ lang, category: group.slug, city: citySlug }}
                aria-label={seeAllAria}
                className="group inline-flex min-h-6 items-center gap-1 text-[13px] font-bold text-[#110E39] transition hover:text-[color:var(--color-primary)] sm:text-[14px]"
              >
                {t("common.see_all")}
                <Icon name="ri-arrow-right-line" className="text-[15px] transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5 sm:text-[16px]" aria-hidden />
              </Link>
              )}
            </div>
          </div>

          {/*
            `overflow-x-auto` computes overflow-y to `auto` too, so this rail
            clips vertically as well as horizontally. The card's shadow is
            `0 4px 20px` at rest and `0 18px 44px` on hover over a 3px lift,
            and there was no room above the card at all: measured on the live
            Marrakech hub, the gap between the rail's top edge and the card's
            was 0px, so the whole upper half of the shadow was sliced off flat
            and read as a hard line across the row.

            The padding buys that room and the negative margin gives it back to
            the layout, so nothing below moves (verified: the next section did
            not shift by a pixel). 32px up is exactly what the hover shadow
            needs; the bottom already had the card's own 20px margin.
          */}
          <div
            ref={shelf.ref}
            className="-mx-4 -mb-6 -mt-8 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-8 pt-8 sm:gap-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {group.items.slice(0, cardsPerGroup).map((a) => (
              <div
                key={a.id}
                className="w-[210px] shrink-0 snap-start sm:w-[230px] lg:w-[250px]"
              >
                <ActivityCardDiscover
                  slug={a.slug}
                  images={a.images}
                  title={a.title}
                  city={a.city.name}
                  citySlug={a.city.slug}
                  category={a.category?.name ?? ""}
                  categorySlug={a.category?.slug}
                  segment={a.segment ?? null}
                  tags={a.tags ?? null}
                  mode={a.mode}
                  price={a.price}
                  isNew={a.isNew === true}
                  typePrice={a.typePrice}
                  rating={a.rating}
                />
              </div>
            ))}

            {!group.composite && (
              <Link
                to="/$lang/category/$category/$city"
                params={{ lang, category: group.slug, city: citySlug }}
                aria-label={seeAllAria}
                className="group hidden w-[230px] shrink-0 snap-start flex-col items-center justify-center gap-3 rounded-[22px] border-2 border-dashed border-[rgba(4,0,51,0.18)] bg-white/60 p-6 text-center transition hover:border-[color:var(--color-primary)] hover:bg-white sm:flex lg:w-[250px]"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--color-primary)] text-white shadow-[0_8px_22px_rgba(4,0,51,0.22)] transition group-hover:scale-105">
                  <Icon name="ri-arrow-right-line" className="text-[22px] rtl:-scale-x-100" aria-hidden />
                </span>
                <div>
                  <p className="text-[15px] font-bold text-[#110E39]">
                    {t("activity.see_all_in_category", { category: label })}
                  </p>
                  <p className="mt-1 text-[12px] font-semibold text-[#5a5d77]">
                    {t("activity.count_in_city", { defaultValue: "{{count}} in {{city}}", count: group.items.length, city: cityName || t("activity.this_city", { defaultValue: "this city" }) })}
                  </p>
                </div>
              </Link>
            )}
          </div>
    </section>
  );
}
